import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Copy, LogOut, Package, BadgePercent, Banknote, TrendingUp, ClipboardList } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AmbassadorPortal = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [showContract, setShowContract] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/ambassadors/me`, { withCredentials: true });
        setMe(data);
      } catch {
        navigate("/ambassador/login");
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${what} copied`);
    } catch {
      toast.error("Copy failed — long-press to copy instead.");
    }
  };

  const onLogout = async () => {
    await axios.post(`${API}/ambassadors/logout`, {}, { withCredentials: true }).catch(() => {});
    navigate("/ambassador/login");
  };

  if (!me) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white font-manrope flex items-center justify-center">
        <p className="font-plex text-xs uppercase tracking-[0.25em] text-white/40">Loading your portal…</p>
      </div>
    );
  }

  const stats = [
    { label: "Sales with your code", value: me.sales, testid: "portal-stat-sales" },
    { label: "Revenue you've driven", value: `A$${me.revenue.toFixed(2)}`, testid: "portal-stat-revenue" },
    { label: "Commission earned", value: `A$${me.commission_earned.toFixed(2)}`, testid: "portal-stat-earned" },
    { label: "Owed to you", value: `A$${me.commission_owed.toFixed(2)}`, testid: "portal-stat-owed", volt: true },
  ];

  return (
    <div data-testid="ambassador-portal" className="min-h-screen bg-[#0A0A0A] text-white font-manrope">
      <header className="sticky top-0 z-40 bg-[#0A0A0A] border-b border-white/10 px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src="/breethe-logo.webp" alt="Breethe" className="h-6 w-auto" />
          <span className="font-plex text-[10px] uppercase tracking-[0.3em] text-volt">Ambassador</span>
        </div>
        <button
          onClick={onLogout}
          data-testid="portal-logout-button"
          className="h-10 px-4 border border-white/15 flex items-center gap-2 font-plex text-xs uppercase tracking-[0.2em] text-white/60 hover:text-volt hover:border-volt transition-colors active:scale-95"
        >
          <LogOut size={14} /> Logout
        </button>
      </header>

      <main className="px-6 md:px-10 py-10 md:py-14 max-w-[1200px] mx-auto">
        <p className="font-plex text-xs uppercase tracking-[0.25em] text-white/40">
          {me.status === "trial" ? "30-day trial partnership" : me.status === "paused" ? "Partnership paused" : "Active ambassador"}
        </p>
        <h1 className="mt-2 font-anton uppercase text-4xl md:text-6xl leading-[0.9]">
          Hey {me.name.split(" ")[0]}<span className="text-volt">.</span>
        </h1>

        {me.status === "paused" && (
          <p data-testid="portal-paused-note" className="mt-4 border border-volt/60 bg-volt/5 px-5 py-4 text-white/70 font-light text-sm">
            Your code is paused right now — sales with it won't earn commission. Questions? Email info@breethe.com.au.
          </p>
        )}

        {/* Code + link */}
        <div data-testid="portal-code-card" className="mt-10 border border-volt bg-volt/5 p-6 md:p-8">
          <p className="font-plex text-xs uppercase tracking-[0.25em] text-white/50">
            Your follower code — {me.follower_pct}% off everything for them, {me.commission_pct}% commission for you
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <span data-testid="portal-code" className="font-anton text-5xl md:text-7xl tracking-wide text-volt">{me.code}</span>
            <button
              onClick={() => copy(me.code, "Code")}
              data-testid="portal-copy-code"
              className="h-11 px-5 border border-white/25 flex items-center gap-2 font-plex text-xs uppercase tracking-[0.2em] text-white/70 hover:text-black hover:bg-white hover:border-white transition-colors active:scale-95"
            >
              <Copy size={14} /> Copy code
            </button>
            <button
              onClick={() => copy(me.link, "Link")}
              data-testid="portal-copy-link"
              className="h-11 px-5 bg-volt text-black flex items-center gap-2 font-plex text-xs uppercase tracking-[0.2em] transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95"
            >
              <Copy size={14} /> Copy your link
            </button>
          </div>
          <p className="mt-4 text-white/50 font-light text-sm break-all">
            {me.link} — opens the site with your code already applied. Perfect for your bio and Stories.
          </p>
        </div>

        {/* Starter pack + expectations */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div data-testid="portal-starter-card" className="border border-white/10 bg-[#121212] p-6">
            <p className="font-anton uppercase text-lg flex items-center gap-3">
              <Package size={20} className="text-volt" /> Starter pack
            </p>
            <p className="mt-3 text-white/60 font-light text-sm">
              2 × Breethe 30-strip packs:{" "}
              <span className={me.starter_pack === "sent" ? "text-volt" : "text-white"}>
                {me.starter_pack === "sent" ? "posted to you" : "being packed — on its way soon"}
              </span>
            </p>
            {me.starter_pack === "sent" && me.starter_tracking && (
              <div className="mt-4 border border-white/10 bg-[#0A0A0A] p-4">
                <p className="font-plex text-[10px] uppercase tracking-[0.25em] text-white/40">Australia Post tracking</p>
                <p data-testid="portal-tracking-number" className="mt-2 font-plex text-sm text-white tracking-[0.15em]">
                  {me.starter_tracking}
                </p>
                <a
                  href={`https://auspost.com.au/mypost/track/#/details/${encodeURIComponent(me.starter_tracking)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="portal-track-link"
                  className="mt-3 inline-block h-10 px-5 bg-volt text-black font-anton uppercase tracking-wider text-xs leading-10 transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95"
                >
                  Track your parcel
                </a>
              </div>
            )}
          </div>
          <div data-testid="portal-deliverables-card" className="border border-white/10 bg-[#121212] p-6">
            <p className="font-anton uppercase text-lg flex items-center gap-3">
              <ClipboardList size={20} className="text-volt" /> Your first 30 days
            </p>
            <ul className="mt-3 space-y-2 text-white/60 font-light text-sm">
              <li>1 genuine Reel or TikTok using Breethe (tag @breetheau / @breethe.au)</li>
              <li>2–3 Stories over the month with your code or link</li>
              <li>Always disclose: #ad or the paid-partnership label</li>
              <li>Honest opinions only — no script, no fake hype</li>
            </ul>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s) => (
            <div key={s.label} data-testid={s.testid} className="border border-white/10 bg-[#121212] p-5 md:p-6">
              <p className="font-plex text-[10px] uppercase tracking-[0.25em] text-white/40">{s.label}</p>
              <p className={`mt-3 font-anton text-2xl md:text-4xl leading-none ${s.volt ? "text-volt" : ""}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Sales */}
        <div data-testid="portal-sales-card" className="mt-10 border border-white/10 bg-[#121212] p-6 md:p-8">
          <p className="font-anton uppercase text-xl md:text-2xl flex items-center gap-3">
            <TrendingUp size={22} className="text-volt" /> Sales with your code
          </p>
          {me.commissions.length > 0 ? (
            <ul data-testid="portal-sales-list" className="mt-5 border-t border-white/10">
              {me.commissions.map((c, i) => (
                <li
                  key={c.id}
                  data-testid={`portal-sale-${i + 1}`}
                  className="border-b border-white/5 py-3.5 flex flex-wrap items-center gap-x-6 gap-y-1"
                >
                  <span className="text-white/70 text-sm font-light">{c.buyer_email}</span>
                  <span className="font-plex text-[10px] uppercase tracking-[0.2em] text-white/40">
                    {new Date(c.created_at).toLocaleDateString("en-AU")}
                  </span>
                  <span className="font-plex text-xs text-white/60">order A${c.order_amount.toFixed(2)}</span>
                  <span className="font-anton text-lg text-volt ml-auto">+A${c.commission.toFixed(2)}</span>
                  <span className={`font-plex text-[10px] uppercase tracking-[0.2em] ${c.paid ? "text-white/30" : "text-volt"}`}>
                    {c.paid ? "Paid" : "Owed"}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-5 font-plex text-[11px] uppercase tracking-[0.2em] text-white/30">
              No sales yet — share your link to get the first one on the board
            </p>
          )}
        </div>

        {/* Payouts */}
        {me.payouts.length > 0 && (
          <div data-testid="portal-payouts-card" className="mt-6 border border-white/10 bg-[#121212] p-6 md:p-8">
            <p className="font-anton uppercase text-xl flex items-center gap-3">
              <Banknote size={20} className="text-volt" /> Payouts
            </p>
            <ul className="mt-4 space-y-2">
              {me.payouts.map((p, i) => (
                <li key={p.id} data-testid={`portal-payout-${i + 1}`} className="flex justify-between font-plex text-xs text-white/60">
                  <span>{new Date(p.created_at).toLocaleDateString("en-AU")} — {p.sales_count} sale{p.sales_count === 1 ? "" : "s"}</span>
                  <span className="text-white">A${p.amount.toFixed(2)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Contract */}
        <div className="mt-6 border border-white/10 bg-[#121212] p-6 md:p-8">
          <button
            onClick={() => setShowContract((v) => !v)}
            data-testid="portal-contract-toggle"
            className="w-full flex items-center justify-between font-anton uppercase text-xl"
          >
            <span className="flex items-center gap-3">
              <BadgePercent size={20} className="text-volt" /> Your agreement
            </span>
            <span className="font-plex text-[10px] tracking-[0.25em] text-white/40">{showContract ? "Hide" : "View"}</span>
          </button>
          {showContract && me.contract && (
            <div data-testid="portal-contract" className="mt-5 max-h-72 overflow-y-auto border border-white/10 bg-[#0A0A0A] p-5 text-white/70 font-light text-sm whitespace-pre-wrap">
              {me.contract.text}
              <p className="mt-4 text-volt font-plex text-xs uppercase tracking-[0.2em]">
                Signed by {me.contract.signature} — {new Date(me.contract.signed_at).toLocaleDateString("en-AU")}
              </p>
            </div>
          )}
        </div>

        <p className="mt-8 text-white/30 font-light text-xs">
          Questions, content ideas or payout details — info@breethe.com.au
        </p>
      </main>
    </div>
  );
};

export default AmbassadorPortal;
